// src/pages/MissionToolkit.jsx - Updated with members as speakers and speaker tab

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    updateDoc,
    getDocs,
    where,
    getDoc,
    increment,
    arrayUnion,
    arrayRemove,
    writeBatch,
    runTransaction
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Swal from 'sweetalert2';
import './MissionToolkit.css';

const MissionToolkit = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();

    // States
    const [loading, setLoading] = useState(true);
    const [members, setMembers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [showVisitationModal, setShowVisitationModal] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [activeTab, setActiveTab] = useState('groups');
    const [searchTerm, setSearchTerm] = useState('');
    const [visitations, setVisitations] = useState([]);
    const [personalVisitations, setPersonalVisitations] = useState([]);
    const [userGroups, setUserGroups] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [groupsRefreshKey, setGroupsRefreshKey] = useState(0);

    // Speaker Management States
    const [speakers, setSpeakers] = useState([]);
    const [speakerAssignments, setSpeakerAssignments] = useState([]);
    const [showSpeakerModal, setShowSpeakerModal] = useState(false);
    const [showSpeakerReportModal, setShowSpeakerReportModal] = useState(false);
    const [editingSpeaker, setEditingSpeaker] = useState(null);
    const [selectedSpeaker, setSelectedSpeaker] = useState(null);
    const [speakerReports, setSpeakerReports] = useState([]);
    const [showPersonalVisitationModal, setShowPersonalVisitationModal] = useState(false);

    // Form states
    const [groupFormData, setGroupFormData] = useState({
        name: '',
        leaderId: '',
        leaderName: '',
        members: [],
        location: '',
        focus: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: ''
    });

    const [visitationFormData, setVisitationFormData] = useState({
        groupId: '',
        peopleCount: '',
        remarks: '',
        challenges: '',
        location: '',
        date: new Date().toISOString().split('T')[0]
    });

    const [personalVisitationFormData, setPersonalVisitationFormData] = useState({
        peopleCount: '',
        remarks: '',
        challenges: '',
        location: '',
        date: new Date().toISOString().split('T')[0],
        ministryArea: 'General'
    });

    const [speakerFormData, setSpeakerFormData] = useState({
        userId: '',
        name: '',
        specialty: '',
        email: '',
        phone: '',
        availability: true,
        assignedTo: []
    });

    const [speakerReportFormData, setSpeakerReportFormData] = useState({
        speakerId: '',
        assignmentId: '',
        date: new Date().toISOString().split('T')[0],
        remarks: '',
        challenges: '',
        peopleReached: '',
        location: '',
        notes: ''
    });

    // Constants for PDF header
    const MINISTRY_NAME = "White Doves Ministry";
    const MINISTRY_ADDRESS = "P.O. Box 43844 - 00100, Nairobi, Kenya";
    const MINISTRY_PHONE = "+254 740086845";
    const MINISTRY_EMAIL = "info.whitedovesministry@gmail.com";
    const LOGO_PATH = "https://res.cloudinary.com/dblffn98e/image/upload/v1771566469/rbonf3jdrbuf5cu2qvbr.png";

    // Speaker specialty options
    const SPEAKER_SPECIALTIES = [
        'Health',
        'Family Life',
        'Bible Study',
        'Sermon',
        'Children',
        'Prophecy',
    ];

    // Load data on mount
    useEffect(() => {
        fetchMembers();
        fetchGroups();
        fetchVisitations();
        fetchPersonalVisitations();
        fetchSpeakers();
        fetchSpeakerAssignments();
        fetchSpeakerReports();
    }, []);

    // Fetch current user's groups
    useEffect(() => {
        if (currentUser && !isAdmin) {
            fetchUserGroups();
        }
    }, [currentUser, groups]);

    const fetchMembers = () => {
        const membersQuery = query(
            collection(db, 'users'),
            orderBy('fullName', 'asc')
        );

        const unsubscribe = onSnapshot(membersQuery, (snapshot) => {
            const membersData = [];
            snapshot.forEach(doc => {
                membersData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            setMembers(membersData);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching members:', error);
            showToast('Error loading members', 'error');
            setLoading(false);
        });

        return unsubscribe;
    };

    const fetchGroups = () => {
        const groupsQuery = query(
            collection(db, 'missionGroups'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(groupsQuery, (snapshot) => {
            const groupsData = [];
            snapshot.forEach(doc => {
                groupsData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            setGroups(groupsData);
        }, (error) => {
            console.error('Error fetching groups:', error);
        });

        return unsubscribe;
    };

    const fetchVisitations = () => {
        try {
            const visitationsQuery = query(
                collection(db, 'missionVisitations')
            );

            const unsubscribe = onSnapshot(visitationsQuery, (snapshot) => {
                const visitationsData = [];
                snapshot.forEach(doc => {
                    visitationsData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                visitationsData.sort((a, b) => {
                    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
                    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
                    return dateB - dateA;
                });
                setVisitations(visitationsData);
            }, (error) => {
                console.error('Error fetching visitations:', error);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up visitations listener:', error);
            return () => {};
        }
    };

    const fetchPersonalVisitations = () => {
        try {
            const personalQuery = query(
                collection(db, 'personalVisitations'),
                orderBy('createdAt', 'desc')
            );

            const unsubscribe = onSnapshot(personalQuery, (snapshot) => {
                const personalData = [];
                snapshot.forEach(doc => {
                    personalData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                setPersonalVisitations(personalData);
            }, (error) => {
                console.error('Error fetching personal visitations:', error);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up personal visitations listener:', error);
            return () => {};
        }
    };

    const fetchSpeakers = () => {
        try {
            const speakersQuery = query(
                collection(db, 'speakers'),
                orderBy('name', 'asc')
            );

            const unsubscribe = onSnapshot(speakersQuery, (snapshot) => {
                const speakersData = [];
                snapshot.forEach(doc => {
                    speakersData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                setSpeakers(speakersData);
            }, (error) => {
                console.error('Error fetching speakers:', error);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up speakers listener:', error);
            return () => {};
        }
    };

    const fetchSpeakerAssignments = () => {
        try {
            const assignmentsQuery = query(
                collection(db, 'speakerAssignments'),
                orderBy('date', 'desc')
            );

            const unsubscribe = onSnapshot(assignmentsQuery, (snapshot) => {
                const assignmentsData = [];
                snapshot.forEach(doc => {
                    assignmentsData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                setSpeakerAssignments(assignmentsData);
            }, (error) => {
                console.error('Error fetching speaker assignments:', error);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up speaker assignments listener:', error);
            return () => {};
        }
    };

    const fetchSpeakerReports = () => {
        try {
            const reportsQuery = query(
                collection(db, 'speakerReports'),
                orderBy('createdAt', 'desc')
            );

            const unsubscribe = onSnapshot(reportsQuery, (snapshot) => {
                const reportsData = [];
                snapshot.forEach(doc => {
                    reportsData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                setSpeakerReports(reportsData);
            }, (error) => {
                console.error('Error fetching speaker reports:', error);
            });

            return unsubscribe;
        } catch (error) {
            console.error('Error setting up speaker reports listener:', error);
            return () => {};
        }
    };

    const fetchUserGroups = () => {
        const userGroupsData = groups.filter(group =>
            group.members?.includes(currentUser.uid) ||
            group.leaderId === currentUser.uid
        );
        setUserGroups(userGroupsData);
    };

    // Get user name by ID
    const getUserName = (userId) => {
        const user = members.find(m => m.id === userId);
        return user?.fullName || user?.firstName || userId || 'Unknown';
    };

    // Get member by ID
    const getMemberById = (userId) => {
        return members.find(m => m.id === userId);
    };

    // Mask phone number
    const maskPhoneNumber = (phone) => {
        if (!phone) return 'N/A';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length < 5) return phone;
        const firstThree = cleaned.substring(0, 3);
        const lastTwo = cleaned.substring(cleaned.length - 2);
        const maskedMiddle = '*'.repeat(Math.min(cleaned.length - 5, 5));
        return `${firstThree}${maskedMiddle}${lastTwo}`;
    };

    // Get total assigned members
    const getAssignedMembersCount = () => {
        const assignedIds = new Set();
        groups.forEach(group => {
            group.members?.forEach(memberId => {
                assignedIds.add(memberId);
            });
        });
        return assignedIds.size;
    };

    // Get assigned members list
    const getAssignedMembers = () => {
        const assignedIds = new Set();
        groups.forEach(group => {
            group.members?.forEach(memberId => {
                assignedIds.add(memberId);
            });
        });
        return members.filter(member => assignedIds.has(member.id));
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    };

    const formatDateTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ====================
    // PDF GENERATION
    // ====================

    const addMinistryHeader = async (doc) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        
        try {
            const logoResponse = await fetch(LOGO_PATH);
            const logoBlob = await logoResponse.blob();
            const logoDataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(logoBlob);
            });
            
            doc.addImage(logoDataUrl, 'PNG', 14, 5, 30, 30);
        } catch (error) {
            console.error('Error loading logo:', error);
        }
        
        doc.setFillColor(16, 52, 166);
        doc.rect(0, 0, pageWidth, 40, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text(MINISTRY_NAME, pageWidth / 2, 20, { align: 'center' });
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('"Arise and Shine" - Isaiah 60:1', pageWidth / 2, 30, { align: 'center' });
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`${MINISTRY_ADDRESS} | Tel: ${MINISTRY_PHONE} | Email: ${MINISTRY_EMAIL}`, pageWidth / 2, 48, { align: 'center' });
        
        doc.setDrawColor(16, 52, 166);
        doc.setLineWidth(0.5);
        doc.line(20, 52, pageWidth - 20, 52);
        
        return 55;
    };

    const addFooter = (doc) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`White Doves Ministry - Missions Report`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    };

    // Export groups to PDF
    const exportGroupsPDF = async () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = await addMinistryHeader(doc);

        doc.setFontSize(16);
        doc.setTextColor(16, 52, 166);
        doc.setFont('helvetica', 'bold');
        doc.text('MISSION GROUPS REPORT', pageWidth / 2, yPos + 10, { align: 'center' });
        
        yPos += 25;
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated By: ${userData?.fullName || 'Admin'}`, 20, yPos);
        doc.text(`Date: ${new Date().toLocaleString()}`, 20, yPos + 8);
        doc.text(`Total Groups: ${groups.length}`, 20, yPos + 16);
        doc.text(`Assigned Members: ${getAssignedMembersCount()}`, 20, yPos + 24);

        const groupsData = groups.map(group => [
            group.name,
            group.members?.length || 0,
            group.leaderName || 'Not Assigned',
            group.totalVisitations || 0,
            group.totalPeopleReached || 0,
            group.status || 'active'
        ]);

        doc.autoTable({
            startY: yPos + 30,
            head: [['Group Name', 'Members', 'Leader', 'Visitations', 'People Reached', 'Status']],
            body: groupsData,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 10 },
            bodyStyles: { fontSize: 9 },
            margin: { left: 20, right: 20 },
            foot: [[
                'TOTAL',
                groups.reduce((sum, g) => sum + (g.members?.length || 0), 0),
                '',
                groups.reduce((sum, g) => sum + (g.totalVisitations || 0), 0),
                groups.reduce((sum, g) => sum + (g.totalPeopleReached || 0), 0),
                ''
            ]],
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', fontSize: 9 }
        });

        addFooter(doc);
        doc.save(`mission-groups-report-${Date.now()}.pdf`);
        showToast('PDF exported successfully!', 'success');
    };

   // Export full mission report - Updated with remarks and challenges
const exportFullReportPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = await addMinistryHeader(doc);

    doc.setFontSize(16);
    doc.setTextColor(16, 52, 166);
    doc.setFont('helvetica', 'bold');
    doc.text('FULL MISSION REPORT', pageWidth / 2, yPos + 10, { align: 'center' });
    
    yPos += 25;
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated By: ${userData?.fullName || 'Admin'}`, 20, yPos);
    doc.text(`Date: ${new Date().toLocaleString()}`, 20, yPos + 8);

    const totalMembers = members.length;
    const assignedMembers = getAssignedMembersCount();
    const totalGroups = groups.length;
    const totalVisitations = visitations.length;
    const totalPeopleReached = visitations.reduce((sum, v) => sum + (v.peopleCount || 0), 0);
    const totalPersonalVisitations = personalVisitations.length;
    const totalPersonalReached = personalVisitations.reduce((sum, v) => sum + (v.peopleCount || 0), 0);

    const statsData = [
        ['Total Ministry Members', totalMembers],
        ['Assigned to Groups', assignedMembers],
        ['Unassigned Members', totalMembers - assignedMembers],
        ['Total Mission Groups', totalGroups],
        ['Group Visitations', totalVisitations],
        ['Group People Reached', totalPeopleReached],
        ['Personal Visitations', totalPersonalVisitations],
        ['Personal People Reached', totalPersonalReached],
        ['Total People Reached', totalPeopleReached + totalPersonalReached]
    ];

    doc.autoTable({
        startY: yPos + 16,
        head: [['Metric', 'Value']],
        body: statsData,
        theme: 'striped',
        headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 10 },
        bodyStyles: { fontSize: 9 },
        margin: { left: 20, right: 20 }
    });

    let finalY = doc.lastAutoTable.finalY + 15;

    // Groups & Members Section
    doc.setFontSize(14);
    doc.setTextColor(16, 52, 166);
    doc.setFont('helvetica', 'bold');
    doc.text('Groups & Members', 20, finalY);
    doc.line(20, finalY + 5, pageWidth - 20, finalY + 5);

    const groupMemberData = groups.map(group => ({
        name: group.name,
        members: group.memberNames?.join(', ') || 'No members',
        leader: group.leaderName || 'Not Assigned',
        visitations: group.totalVisitations || 0,
        peopleReached: group.totalPeopleReached || 0
    }));

    doc.autoTable({
        startY: finalY + 12,
        head: [['Group Name', 'Members', 'Leader', 'Visitations', 'People Reached']],
        body: groupMemberData.map(g => [g.name, g.members, g.leader, g.visitations, g.peopleReached]),
        theme: 'striped',
        headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 10 },
        bodyStyles: { fontSize: 9 },
        margin: { left: 20, right: 20 }
    });

    finalY = doc.lastAutoTable.finalY + 15;

    // Group Visitations with Remarks and Challenges
    doc.setFontSize(14);
    doc.setTextColor(16, 52, 166);
    doc.setFont('helvetica', 'bold');
    doc.text('Group Visitation Details', 20, finalY);
    doc.line(20, finalY + 5, pageWidth - 20, finalY + 5);

    if (visitations.length > 0) {
        const visitationDetails = visitations.map(v => [
            formatDate(v.date),
            v.groupName || 'N/A',
            v.peopleCount || 0,
            v.location || 'N/A',
            v.remarks || 'N/A',
            v.challenges || 'N/A',
            v.recordedByName || 'Unknown'
        ]);

        doc.autoTable({
            startY: finalY + 12,
            head: [['Date', 'Group', 'People', 'Location', 'Remarks', 'Challenges', 'Recorded By']],
            body: visitationDetails,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 20, right: 20 },
            columnStyles: {
                4: { cellWidth: 'auto', fontSize: 7 },
                5: { cellWidth: 'auto', fontSize: 7 }
            },
            foot: [[
                'TOTAL',
                visitations.length + ' visitations',
                visitations.reduce((sum, v) => sum + (v.peopleCount || 0), 0),
                '', '', '', ''
            ]],
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', fontSize: 8 }
        });
    } else {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('No group visitations recorded.', 20, finalY + 12);
    }

    finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : finalY + 30;

    // Personal Visitations with Remarks and Challenges
    doc.setFontSize(14);
    doc.setTextColor(16, 52, 166);
    doc.setFont('helvetica', 'bold');
    doc.text('Personal Visitation Details', 20, finalY);
    doc.line(20, finalY + 5, pageWidth - 20, finalY + 5);

    if (personalVisitations.length > 0) {
        const personalDetails = personalVisitations.map(v => [
            formatDate(v.date),
            v.userName || 'Unknown',
            v.peopleCount || 0,
            v.ministryArea || 'General',
            v.location || 'N/A',
            v.remarks || 'N/A',
            v.challenges || 'N/A'
        ]);

        doc.autoTable({
            startY: finalY + 12,
            head: [['Date', 'Member', 'People', 'Ministry Area', 'Location', 'Remarks', 'Challenges']],
            body: personalDetails,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 20, right: 20 },
            columnStyles: {
                5: { cellWidth: 'auto', fontSize: 7 },
                6: { cellWidth: 'auto', fontSize: 7 }
            },
            foot: [[
                'TOTAL',
                personalVisitations.length + ' records',
                personalVisitations.reduce((sum, v) => sum + (v.peopleCount || 0), 0),
                '', '', '', ''
            ]],
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', fontSize: 8 }
        });
    } else {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('No personal visitations recorded.', 20, finalY + 12);
    }

    finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : finalY + 30;

    // Speaker Reports with Remarks and Challenges
    if (speakerReports.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(16, 52, 166);
        doc.setFont('helvetica', 'bold');
        doc.text('Speaker Reports', 20, finalY);
        doc.line(20, finalY + 5, pageWidth - 20, finalY + 5);

        const speakerReportDetails = speakerReports.map(r => {
            const speaker = speakers.find(s => s.id === r.speakerId);
            return [
                formatDate(r.date),
                speaker?.name || 'Unknown',
                r.location || 'N/A',
                r.remarks || 'N/A',
                r.challenges || 'N/A'
            ];
        });

        doc.autoTable({
            startY: finalY + 12,
            head: [['Date', 'Speaker', 'People Reached', 'Location', 'Remarks', 'Challenges']],
            body: speakerReportDetails,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 20, right: 20 },
            columnStyles: {
                4: { cellWidth: 'auto', fontSize: 7 },
                5: { cellWidth: 'auto', fontSize: 7 }
            },
            foot: [[
                'TOTAL',
                speakerReports.length + ' reports',
                speakerReports.reduce((sum, r) => sum + (r.peopleReached || 0), 0),
                '', '', ''
            ]],
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', fontSize: 8 }
        });
    }

    addFooter(doc);
    doc.save(`full-mission-report-${Date.now()}.pdf`);
    showToast('Full report exported successfully!', 'success');
};
    

    // Export Attendees List
    const exportAttendeesPDF = async () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = await addMinistryHeader(doc);

        doc.setFontSize(16);
        doc.setTextColor(16, 52, 166);
        doc.setFont('helvetica', 'bold');
        doc.text('ATTENDEES LIST', pageWidth / 2, yPos + 10, { align: 'center' });
        
        yPos += 25;
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated By: ${userData?.fullName || 'Admin'}`, 20, yPos);
        doc.text(`Date: ${new Date().toLocaleString()}`, 20, yPos + 8);
        
        const assignedMembers = getAssignedMembers();
        doc.text(`Total Attendees: ${assignedMembers.length}`, 20, yPos + 16);

        const attendeesData = assignedMembers.map(member => [
            member.fullName || member.firstName || 'Unknown',
            member.yearName || 'N/A',
            maskPhoneNumber(member.phone)
        ]);

        doc.autoTable({
            startY: yPos + 24,
            head: [['Name', 'Year Group', 'Phone Number']],
            body: attendeesData,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 10 },
            bodyStyles: { fontSize: 9 },
            margin: { left: 20, right: 20 },
            foot: [[
                'TOTAL: ' + assignedMembers.length,
                '', ''
            ]],
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', fontSize: 9 }
        });

        addFooter(doc);
        doc.save(`attendees-list-${Date.now()}.pdf`);
        showToast('Attendees list exported successfully!', 'success');
    };

    // Export single group report
    const exportGroupReport = async (groupId) => {
        const group = groups.find(g => g.id === groupId);
        if (!group) return;

        const groupVisitations = visitations.filter(v => v.groupId === groupId);

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = await addMinistryHeader(doc);

        doc.setFontSize(16);
        doc.setTextColor(16, 52, 166);
        doc.setFont('helvetica', 'bold');
        doc.text('GROUP REPORT', pageWidth / 2, yPos + 10, { align: 'center' });
        
        yPos += 25;
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(`Group: ${group.name}`, 20, yPos);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Leader: ${group.leaderName || 'Not Assigned'}`, 20, yPos + 10);
        doc.text(`Members: ${group.memberNames?.join(', ') || 'None'}`, 20, yPos + 18);
        doc.text(`Total Visitations: ${group.totalVisitations || 0}`, 20, yPos + 26);
        doc.text(`Total People Reached: ${group.totalPeopleReached || 0}`, 20, yPos + 34);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 20, yPos + 42);

        const visitationData = groupVisitations.map(v => [
            formatDate(v.date),
            v.peopleCount,
            v.remarks || '',
            v.challenges || '',
            v.location || '',
            v.recordedByName
        ]);

        doc.autoTable({
            startY: yPos + 48,
            head: [['Date', 'People', 'Remarks', 'Challenges', 'Location', 'Recorded By']],
            body: visitationData,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 20, right: 20 },
            foot: [[
                'TOTAL',
                groupVisitations.reduce((sum, v) => sum + (v.peopleCount || 0), 0),
                '', '', '', ''
            ]],
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', fontSize: 9 }
        });

        addFooter(doc);
        doc.save(`group-${group.name}-report-${Date.now()}.pdf`);
        showToast('Group report exported successfully!', 'success');
    };

    // Export Speaker Report
    const exportSpeakerReportPDF = async (speakerId) => {
        const speaker = speakers.find(s => s.id === speakerId);
        if (!speaker) return;

        const speakerReportsData = speakerReports.filter(r => r.speakerId === speakerId);
        const assignments = speakerAssignments.filter(a => a.speakerId === speakerId);

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = await addMinistryHeader(doc);

        doc.setFontSize(16);
        doc.setTextColor(16, 52, 166);
        doc.setFont('helvetica', 'bold');
        doc.text('SPEAKER REPORT', pageWidth / 2, yPos + 10, { align: 'center' });
        
        yPos += 25;
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(`Speaker: ${speaker.name}`, 20, yPos);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Specialty: ${speaker.specialty || 'General'}`, 20, yPos + 10);
        doc.text(`Total Assignments: ${assignments.length}`, 20, yPos + 18);
        doc.text(`Total Reports: ${speakerReportsData.length}`, 20, yPos + 26);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 20, yPos + 34);

        const reportsData = speakerReportsData.map(r => [
            formatDate(r.date),
            r.peopleReached || 0,
            r.remarks || '',
            r.challenges || '',
            r.location || '',
            formatDateTime(r.createdAt)
        ]);

        doc.autoTable({
            startY: yPos + 42,
            head: [['Date', 'People Reached', 'Remarks', 'Challenges', 'Location', 'Reported']],
            body: reportsData,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            margin: { left: 20, right: 20 },
            foot: [[
                'TOTAL',
                speakerReportsData.reduce((sum, r) => sum + (r.peopleReached || 0), 0),
                '', '', '', ''
            ]],
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', fontSize: 9 }
        });

        addFooter(doc);
        doc.save(`speaker-${speaker.name}-report-${Date.now()}.pdf`);
        showToast('Speaker report exported successfully!', 'success');
    };

    // Handle group creation/update
    const handleGroupSubmit = async (e) => {
        e.preventDefault();

        if (!groupFormData.name.trim()) {
            showToast('Please enter a group name', 'error');
            return;
        }

        if (groupFormData.members.length === 0) {
            showToast('Please select at least one member', 'error');
            return;
        }

        if (groupFormData.members.length > 4) {
            showToast('Group cannot have more than 4 members', 'error');
            return;
        }

        setLoading(true);

        try {
            const adminName = await getAdminName();
            const groupData = {
                name: groupFormData.name.trim(),
                leaderId: groupFormData.leaderId,
                leaderName: groupFormData.leaderId ? getUserName(groupFormData.leaderId) : '',
                members: groupFormData.members,
                memberNames: groupFormData.members.map(id => getUserName(id)),
                location: groupFormData.location || '',
                focus: groupFormData.focus || '',
                startDate: groupFormData.startDate,
                endDate: groupFormData.endDate || '',
                totalVisitations: 0,
                totalPeopleReached: 0,
                status: 'active',
                updatedBy: currentUser.uid,
                updatedByName: adminName,
                updatedAt: serverTimestamp()
            };

            if (editingGroup) {
                const groupRef = doc(db, 'missionGroups', editingGroup.id);
                await updateDoc(groupRef, groupData);
                showToast('Group updated successfully!', 'success');
            } else {
                groupData.createdAt = serverTimestamp();
                groupData.createdBy = currentUser.uid;
                groupData.createdByName = adminName;
                await addDoc(collection(db, 'missionGroups'), groupData);
                showToast('Group created successfully!', 'success');
            }

            setShowGroupModal(false);
            resetGroupForm();
            setGroupsRefreshKey(prev => prev + 1);
        } catch (error) {
            console.error('Error saving group:', error);
            showToast('Error saving group', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle group visitation submission
    const handleVisitationSubmit = async (e) => {
        e.preventDefault();

        if (!visitationFormData.peopleCount || parseInt(visitationFormData.peopleCount) <= 0) {
            showToast('Please enter number of people visited', 'error');
            return;
        }

        if (!visitationFormData.groupId) {
            showToast('Please select a group', 'error');
            return;
        }

        setLoading(true);

        try {
            const group = groups.find(g => g.id === visitationFormData.groupId);
            if (!group) {
                showToast('Selected group not found', 'error');
                setLoading(false);
                return;
            }

            const recordedByName = userData?.fullName || userData?.firstName || currentUser.email;
            const peopleCount = parseInt(visitationFormData.peopleCount);

            await runTransaction(db, async (transaction) => {
                const groupRef = doc(db, 'missionGroups', group.id);
                const groupDoc = await transaction.get(groupRef);
                
                if (!groupDoc.exists()) {
                    throw new Error('Group does not exist!');
                }

                const visitationRef = doc(collection(db, 'missionVisitations'));
                const visitationData = {
                    groupId: visitationFormData.groupId,
                    groupName: group.name,
                    peopleCount: peopleCount,
                    remarks: visitationFormData.remarks || '',
                    challenges: visitationFormData.challenges || '',
                    location: visitationFormData.location || group.location || '',
                    date: visitationFormData.date || new Date().toISOString().split('T')[0],
                    recordedBy: currentUser.uid,
                    recordedByName: recordedByName,
                    createdAt: serverTimestamp()
                };
                
                transaction.set(visitationRef, visitationData);
                transaction.update(groupRef, {
                    totalVisitations: increment(1),
                    totalPeopleReached: increment(peopleCount),
                    lastVisitation: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
            });

            showToast('Visitation recorded successfully!', 'success');
            setShowVisitationModal(false);
            resetVisitationForm();
            setRefreshKey(prev => prev + 1);
            setGroupsRefreshKey(prev => prev + 1);

        } catch (error) {
            console.error('Error recording visitation:', error);
            showToast('Error recording visitation: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle personal visitation submission
    const handlePersonalVisitationSubmit = async (e) => {
        e.preventDefault();

        if (!personalVisitationFormData.peopleCount || parseInt(personalVisitationFormData.peopleCount) <= 0) {
            showToast('Please enter number of people reached', 'error');
            return;
        }

        setLoading(true);

        try {
            const recordedByName = userData?.fullName || userData?.firstName || currentUser.email;
            const peopleCount = parseInt(personalVisitationFormData.peopleCount);

            const personalData = {
                userId: currentUser.uid,
                userName: recordedByName,
                peopleCount: peopleCount,
                remarks: personalVisitationFormData.remarks || '',
                challenges: personalVisitationFormData.challenges || '',
                location: personalVisitationFormData.location || '',
                date: personalVisitationFormData.date || new Date().toISOString().split('T')[0],
                ministryArea: personalVisitationFormData.ministryArea || 'General',
                createdAt: serverTimestamp()
            };

            await addDoc(collection(db, 'personalVisitations'), personalData);
            showToast('Personal visitation recorded successfully!', 'success');
            setShowPersonalVisitationModal(false);
            resetPersonalVisitationForm();
            setRefreshKey(prev => prev + 1);

        } catch (error) {
            console.error('Error recording personal visitation:', error);
            showToast('Error recording personal visitation: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle speaker management
    const handleSpeakerSubmit = async (e) => {
        e.preventDefault();

        if (!speakerFormData.userId) {
            showToast('Please select a member to add as speaker', 'error');
            return;
        }

        setLoading(true);

        try {
            const member = getMemberById(speakerFormData.userId);
            if (!member) {
                showToast('Selected member not found', 'error');
                setLoading(false);
                return;
            }

            const speakerData = {
                userId: speakerFormData.userId,
                name: member.fullName || member.firstName || 'Unknown',
                email: member.email || '',
                phone: member.phone || '',
                specialty: speakerFormData.specialty || '',
                availability: speakerFormData.availability !== undefined ? speakerFormData.availability : true,
                assignedTo: speakerFormData.assignedTo || [],
                createdBy: currentUser.uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            if (editingSpeaker) {
                const speakerRef = doc(db, 'speakers', editingSpeaker.id);
                await updateDoc(speakerRef, speakerData);
                showToast('Speaker updated successfully!', 'success');
            } else {
                // Check if member is already a speaker
                const existingSpeaker = speakers.find(s => s.userId === speakerFormData.userId);
                if (existingSpeaker) {
                    showToast('This member is already a speaker', 'error');
                    setLoading(false);
                    return;
                }
                await addDoc(collection(db, 'speakers'), speakerData);
                showToast('Speaker added successfully!', 'success');
            }

            setShowSpeakerModal(false);
            resetSpeakerForm();
        } catch (error) {
            console.error('Error saving speaker:', error);
            showToast('Error saving speaker', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle speaker report submission
    const handleSpeakerReportSubmit = async (e) => {
        e.preventDefault();

        if (!speakerReportFormData.speakerId) {
            showToast('Please select a speaker', 'error');
            return;
        }

        setLoading(true);

        try {
            const reportData = {
                speakerId: speakerReportFormData.speakerId,
                date: speakerReportFormData.date || new Date().toISOString().split('T')[0],
                remarks: speakerReportFormData.remarks || '',
                challenges: speakerReportFormData.challenges || '',
                peopleReached: parseInt(speakerReportFormData.peopleReached) || 0,
                location: speakerReportFormData.location || '',
                notes: speakerReportFormData.notes || '',
                reportedBy: currentUser.uid,
                reportedByName: userData?.fullName || currentUser.email,
                createdAt: serverTimestamp()
            };

            await addDoc(collection(db, 'speakerReports'), reportData);
            showToast('Speaker report submitted successfully!', 'success');
            setShowSpeakerReportModal(false);
            resetSpeakerReportForm();
        } catch (error) {
            console.error('Error submitting speaker report:', error);
            showToast('Error submitting speaker report', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Get admin name
    const getAdminName = async () => {
        try {
            const adminDoc = await getDocs(query(
                collection(db, 'admins'),
                where('email', '==', currentUser.email)
            ));
            if (!adminDoc.empty) {
                const adminData = adminDoc.docs[0].data();
                return adminData.fullName || adminData.name || currentUser.email;
            }
            return userData?.fullName || currentUser.email;
        } catch {
            return userData?.fullName || currentUser.email;
        }
    };

    // Reset forms
    const resetGroupForm = () => {
        setGroupFormData({
            name: '',
            leaderId: '',
            leaderName: '',
            members: [],
            location: '',
            focus: '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: ''
        });
        setEditingGroup(null);
    };

    const resetVisitationForm = () => {
        setVisitationFormData({
            groupId: '',
            peopleCount: '',
            remarks: '',
            challenges: '',
            location: '',
            date: new Date().toISOString().split('T')[0]
        });
    };

    const resetPersonalVisitationForm = () => {
        setPersonalVisitationFormData({
            peopleCount: '',
            remarks: '',
            challenges: '',
            location: '',
            date: new Date().toISOString().split('T')[0],
            ministryArea: 'General'
        });
    };

    const resetSpeakerForm = () => {
        setSpeakerFormData({
            userId: '',
            name: '',
            specialty: '',
            email: '',
            phone: '',
            availability: true,
            assignedTo: []
        });
        setEditingSpeaker(null);
    };

    const resetSpeakerReportForm = () => {
        setSpeakerReportFormData({
            speakerId: '',
            assignmentId: '',
            date: new Date().toISOString().split('T')[0],
            remarks: '',
            challenges: '',
            peopleReached: '',
            location: '',
            notes: ''
        });
    };

    // Handle edit group
    const handleEditGroup = (group) => {
        setEditingGroup(group);
        setGroupFormData({
            name: group.name || '',
            leaderId: group.leaderId || '',
            leaderName: group.leaderName || '',
            members: group.members || [],
            location: group.location || '',
            focus: group.focus || '',
            startDate: group.startDate || new Date().toISOString().split('T')[0],
            endDate: group.endDate || ''
        });
        setShowGroupModal(true);
    };

    // Handle edit speaker
    const handleEditSpeaker = (speaker) => {
        setEditingSpeaker(speaker);
        setSpeakerFormData({
            userId: speaker.userId || '',
            name: speaker.name || '',
            specialty: speaker.specialty || '',
            email: speaker.email || '',
            phone: speaker.phone || '',
            availability: speaker.availability !== undefined ? speaker.availability : true,
            assignedTo: speaker.assignedTo || []
        });
        setShowSpeakerModal(true);
    };

    // Handle delete speaker
    const handleDeleteSpeaker = async (speakerId) => {
        const result = await Swal.fire({
            title: 'Remove Speaker?',
            text: 'This will remove the member from the speakers list. Their reports will be preserved.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, remove it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'speakers', speakerId));
                showToast('Speaker removed successfully!', 'success');
            } catch (error) {
                console.error('Error removing speaker:', error);
                showToast('Error removing speaker', 'error');
            }
        }
    };

    // Handle delete group
    const handleDeleteGroup = async (groupId) => {
        const result = await Swal.fire({
            title: 'Delete Group?',
            text: 'This will also delete all visitation records for this group. This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const visitationsQuery = query(
                    collection(db, 'missionVisitations'),
                    where('groupId', '==', groupId)
                );
                const snapshot = await getDocs(visitationsQuery);
                const batch = writeBatch(db);
                snapshot.docs.forEach(doc => {
                    batch.delete(doc.ref);
                });
                batch.delete(doc(db, 'missionGroups', groupId));
                await batch.commit();

                showToast('Group and all visitations deleted successfully!', 'success');
                setGroupsRefreshKey(prev => prev + 1);
            } catch (error) {
                console.error('Error deleting group:', error);
                showToast('Error deleting group', 'error');
            }
        }
    };

    // Get filtered groups
    const filteredGroups = groups.filter(group =>
        group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.leaderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.memberNames?.some(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Get user's personal visitations
    const getUserPersonalVisitations = () => {
        return personalVisitations.filter(v => v.userId === currentUser.uid);
    };

    // Get user's speaker assignments
    const getUserSpeakerAssignments = () => {
        return speakerAssignments.filter(a => a.speakerId === currentUser.uid);
    };

    // Check if user is a speaker
    const isUserSpeaker = () => {
        return speakers.some(s => s.userId === currentUser.uid);
    };

    // Get user's speaker profile
    const getUserSpeakerProfile = () => {
        return speakers.find(s => s.userId === currentUser.uid);
    };

    // Get speaker's reports
    const getSpeakerReports = (speakerId) => {
        return speakerReports.filter(r => r.speakerId === speakerId);
    };

    // Render speaker tab content
    const renderSpeakerTab = () => {
        const userSpeaker = getUserSpeakerProfile();
        const userReports = userSpeaker ? getSpeakerReports(userSpeaker.id) : [];

        return (
            <div className="mt-speaker-dashboard">
                <div className="mt-speaker-header-section">
                    <h2>Speaker Dashboard</h2>
                    {userSpeaker && (
                        <button
                            className="mt-btn-primary"
                            onClick={() => {
                                setSpeakerReportFormData({
                                    ...speakerReportFormData,
                                    speakerId: userSpeaker.id
                                });
                                setShowSpeakerReportModal(true);
                            }}
                        >
                            <i className="fas fa-plus"></i> Submit Report
                        </button>
                    )}
                </div>

                {!userSpeaker ? (
                    <div className="mt-empty-state">
                        <i className="fas fa-microphone"></i>
                        <p>You are not registered as a speaker.</p>
                        <p className="mt-empty-sub">Contact the administrator to be added as a speaker.</p>
                    </div>
                ) : (
                    <>
                        {/* Speaker Profile Card */}
                        <div className="mt-speaker-profile-card">
                            <div className="mt-speaker-profile-header">
                                <div className="mt-speaker-avatar">
                                    <i className="fas fa-user-circle"></i>
                                </div>
                                <div className="mt-speaker-profile-info">
                                    <h3>{userSpeaker.name}</h3>
                                    <div className="mt-speaker-profile-details">
                                        <span className="mt-speaker-specialty">
                                            <i className="fas fa-tag"></i> {userSpeaker.specialty || 'General Speaker'}
                                        </span>
                                        <span className={`mt-speaker-status ${userSpeaker.availability ? 'available' : 'unavailable'}`}>
                                            {userSpeaker.availability ? 'Available' : 'Unavailable'}
                                        </span>
                                    </div>
                                    {userSpeaker.email && (
                                        <p><i className="fas fa-envelope"></i> {userSpeaker.email}</p>
                                    )}
                                    {userSpeaker.phone && (
                                        <p><i className="fas fa-phone"></i> {userSpeaker.phone}</p>
                                    )}
                                </div>
                            </div>
                            <div className="mt-speaker-stats">
                                <div className="mt-stat-item">
                                    <span className="mt-stat-number">{speakerAssignments.filter(a => a.speakerId === userSpeaker.id).length}</span>
                                    <span className="mt-stat-label">Assignments</span>
                                </div>
                                <div className="mt-stat-item">
                                    <span className="mt-stat-number">{userReports.length}</span>
                                    <span className="mt-stat-label">Reports</span>
                                </div>
                                <div className="mt-stat-item">
                                    <span className="mt-stat-number">{userReports.reduce((sum, r) => sum + (r.peopleReached || 0), 0)}</span>
                                    <span className="mt-stat-label">People Reached</span>
                                </div>
                            </div>
                        </div>

                        {/* Speaker Assignments */}
                        <div className="mt-speaker-assignments">
                            <h3>My Assignments</h3>
                            {speakerAssignments.filter(a => a.speakerId === userSpeaker.id).length === 0 ? (
                                <div className="mt-empty-state-small">
                                    <p>No assignments yet.</p>
                                </div>
                            ) : (
                                <div className="mt-recent-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Location</th>
                                                <th>Assigned By</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {speakerAssignments
                                                .filter(a => a.speakerId === userSpeaker.id)
                                                .map(a => (
                                                    <tr key={a.id}>
                                                        <td>{formatDate(a.date)}</td>
                                                        <td>{a.location || 'N/A'}</td>
                                                        <td>{a.assignedByName || 'Admin'}</td>
                                                        <td>
                                                            <span className={`mt-status-badge ${a.status || 'assigned'}`}>
                                                                {a.status || 'Assigned'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Speaker Reports */}
                        <div className="mt-speaker-reports-list">
                            <h3>My Reports</h3>
                            {userReports.length === 0 ? (
                                <div className="mt-empty-state-small">
                                    <p>No reports submitted yet.</p>
                                </div>
                            ) : (
                                <div className="mt-recent-table">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Remarks</th>
                                                <th>Challenges</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {userReports.map(r => (
                                                <tr key={r.id}>
                                                    <td>{formatDate(r.date)}</td>
                                                    <td>{r.remarks || 'N/A'}</td>
                                                    <td>{r.challenges || 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    // Render admin dashboard
    const renderAdminDashboard = () => (
        <div className="mt-admin-dashboard">
            <div className="mt-stats-grid">
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-users"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">{members.length}</div>
                        <div className="mt-stat-label">Total Members</div>
                    </div>
                </div>
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-user-check"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">{getAssignedMembersCount()}</div>
                        <div className="mt-stat-label">Assigned to Groups</div>
                    </div>
                </div>
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-layer-group"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">{groups.length}</div>
                        <div className="mt-stat-label">Active Groups</div>
                    </div>
                </div>
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-walking"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">{visitations.length}</div>
                        <div className="mt-stat-label">Group Visitations</div>
                    </div>
                </div>
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-user-plus"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">{personalVisitations.length}</div>
                        <div className="mt-stat-label">Personal Visitations</div>
                    </div>
                </div>
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-microphone"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">{speakers.length}</div>
                        <div className="mt-stat-label">Speakers</div>
                    </div>
                </div>
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-users"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">
                            {visitations.reduce((sum, v) => sum + (v.peopleCount || 0), 0) +
                             personalVisitations.reduce((sum, v) => sum + (v.peopleCount || 0), 0)}
                        </div>
                        <div className="mt-stat-label">Total People Reached</div>
                    </div>
                </div>
            </div>

            <div className="mt-actions-bar">
                <button
                    className="mt-btn-primary"
                    onClick={() => {
                        resetGroupForm();
                        setShowGroupModal(true);
                    }}
                >
                    <i className="fas fa-plus"></i> Create Group
                </button>
                <button
                    className="mt-btn-success"
                    onClick={() => {
                        resetSpeakerForm();
                        setShowSpeakerModal(true);
                    }}
                >
                    <i className="fas fa-microphone"></i> Add Speaker
                </button>
                <button
                    className="mt-btn-secondary"
                    onClick={exportGroupsPDF}
                >
                    <i className="fas fa-file-pdf"></i> Export Groups
                </button>
                <button
                    className="mt-btn-info"
                    onClick={exportFullReportPDF}
                >
                    <i className="fas fa-file-pdf"></i> Full Report
                </button>
                <button
                    className="mt-btn-warning"
                    onClick={exportAttendeesPDF}
                >
                    <i className="fas fa-file-pdf"></i> Attendees List
                </button>
            </div>

            <div className="mt-groups-section">
                <h2>Mission Groups</h2>
                <div className="mt-search-bar">
                    <input
                        type="text"
                        placeholder="Search groups..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="fas fa-search"></i>
                </div>

                <div className="mt-groups-grid">
                    {filteredGroups.length === 0 ? (
                        <div className="mt-empty-state">
                            <i className="fas fa-layer-group"></i>
                            <p>No groups found. Create your first group!</p>
                        </div>
                    ) : (
                        filteredGroups.map(group => (
                            <div key={group.id} className="mt-group-card">
                                <div className="mt-group-header">
                                    <h3>{group.name}</h3>
                                    <span className={`mt-group-status ${group.status}`}>
                                        {group.status || 'active'}
                                    </span>
                                </div>
                                <div className="mt-group-body">
                                    <div className="mt-group-info">
                                        <div className="mt-group-info-item">
                                            <i className="fas fa-user-tie"></i>
                                            <span><strong>Leader:</strong> {group.leaderName || 'Not Assigned'}</span>
                                        </div>
                                        <div className="mt-group-info-item">
                                            <i className="fas fa-users"></i>
                                            <span><strong>Members:</strong> {group.memberNames?.join(', ') || 'None'}</span>
                                        </div>
                                        <div className="mt-group-info-item">
                                            <i className="fas fa-walking"></i>
                                            <span><strong>Visitations:</strong> {group.totalVisitations || 0}</span>
                                        </div>
                                        <div className="mt-group-info-item">
                                            <i className="fas fa-user-friends"></i>
                                            <span><strong>People Reached:</strong> {group.totalPeopleReached || 0}</span>
                                        </div>
                                        {group.location && (
                                            <div className="mt-group-info-item">
                                                <i className="fas fa-map-marker-alt"></i>
                                                <span><strong>Location:</strong> {group.location}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-group-actions">
                                        <button
                                            className="mt-btn-small mt-btn-view"
                                            onClick={() => {
                                                setSelectedGroup(group);
                                                setShowMemberModal(true);
                                            }}
                                        >
                                            <i className="fas fa-eye"></i> View
                                        </button>
                                        <button
                                            className="mt-btn-small mt-btn-edit"
                                            onClick={() => handleEditGroup(group)}
                                        >
                                            <i className="fas fa-edit"></i> Edit
                                        </button>
                                        <button
                                            className="mt-btn-small mt-btn-export"
                                            onClick={() => exportGroupReport(group.id)}
                                        >
                                            <i className="fas fa-file-pdf"></i> Report
                                        </button>
                                        <button
                                            className="mt-btn-small mt-btn-delete"
                                            onClick={() => handleDeleteGroup(group.id)}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Speakers Section */}
            <div className="mt-speakers-section">
                <h2>Speakers</h2>
                <div className="mt-speakers-grid">
                    {speakers.length === 0 ? (
                        <div className="mt-empty-state">
                            <i className="fas fa-microphone"></i>
                            <p>No speakers added yet.</p>
                            <button
                                className="mt-btn-primary mt-btn-sm"
                                onClick={() => {
                                    resetSpeakerForm();
                                    setShowSpeakerModal(true);
                                }}
                            >
                                <i className="fas fa-plus"></i> Add Speaker
                            </button>
                        </div>
                    ) : (
                        speakers.map(speaker => {
                            const member = getMemberById(speaker.userId);
                            return (
                                <div key={speaker.id} className="mt-speaker-card">
                                    <div className="mt-speaker-header">
                                        <h3>{speaker.name}</h3>
                                        <span className={`mt-speaker-status ${speaker.availability ? 'available' : 'unavailable'}`}>
                                            {speaker.availability ? 'Available' : 'Unavailable'}
                                        </span>
                                    </div>
                                    <div className="mt-speaker-body">
                                        <div className="mt-speaker-info">
                                            {speaker.specialty && (
                                                <div className="mt-speaker-info-item">
                                                    <i className="fas fa-tag"></i>
                                                    <span><strong>Specialty:</strong> {speaker.specialty}</span>
                                                </div>
                                            )}
                                            {speaker.email && (
                                                <div className="mt-speaker-info-item">
                                                    <i className="fas fa-envelope"></i>
                                                    <span>{speaker.email}</span>
                                                </div>
                                            )}
                                            {speaker.phone && (
                                                <div className="mt-speaker-info-item">
                                                    <i className="fas fa-phone"></i>
                                                    <span>{speaker.phone}</span>
                                                </div>
                                            )}
                                            <div className="mt-speaker-info-item">
                                                <i className="fas fa-user"></i>
                                                <span><strong>Member:</strong> {member?.fullName || member?.firstName || 'Unknown'}</span>
                                            </div>
                                            <div className="mt-speaker-info-item">
                                                <i className="fas fa-tasks"></i>
                                                <span><strong>Assignments:</strong> {
                                                    speakerAssignments.filter(a => a.speakerId === speaker.id).length
                                                }</span>
                                            </div>
                                            <div className="mt-speaker-info-item">
                                                <i className="fas fa-file-alt"></i>
                                                <span><strong>Reports:</strong> {
                                                    speakerReports.filter(r => r.speakerId === speaker.id).length
                                                }</span>
                                            </div>
                                        </div>
                                        <div className="mt-speaker-actions">
                                            <button
                                                className="mt-btn-small mt-btn-edit"
                                                onClick={() => handleEditSpeaker(speaker)}
                                            >
                                                <i className="fas fa-edit"></i> Edit
                                            </button>
                                            <button
                                                className="mt-btn-small mt-btn-export"
                                                onClick={() => exportSpeakerReportPDF(speaker.id)}
                                            >
                                                <i className="fas fa-file-pdf"></i> Report
                                            </button>
                                            <button
                                                className="mt-btn-small mt-btn-delete"
                                                onClick={() => handleDeleteSpeaker(speaker.id)}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                            <button
                                                className="mt-btn-small mt-btn-info"
                                                onClick={() => {
                                                    // Assign speaker to event
                                                    Swal.fire({
                                                        title: 'Assign Speaker',
                                                        html: `
                                                            <div style="text-align:left">
                                                                <label style="display:block;margin-bottom:5px">Event Date</label>
                                                                <input id="assign-date" class="swal2-input" type="date" value="${new Date().toISOString().split('T')[0]}">
                                                                <label style="display:block;margin-bottom:5px">Location</label>
                                                                <input id="assign-location" class="swal2-input" placeholder="Event Location">
                                                            </div>
                                                        `,
                                                        showCancelButton: true,
                                                        confirmButtonText: 'Assign',
                                                        preConfirm: () => {
                                                            const date = document.getElementById('assign-date').value;
                                                            const location = document.getElementById('assign-location').value;
                                                            if (!date || !location) {
                                                                Swal.showValidationMessage('Please fill all fields');
                                                                return false;
                                                            }
                                                            return { date, location };
                                                        }
                                                    }).then(async (result) => {
                                                        if (result.isConfirmed) {
                                                            try {
                                                                const assignmentData = {
                                                                    speakerId: speaker.id,
                                                                    speakerName: speaker.name,
                                                                    date: result.value.date,
                                                                    location: result.value.location,
                                                                    assignedBy: currentUser.uid,
                                                                    assignedByName: userData?.fullName || currentUser.email,
                                                                    createdAt: serverTimestamp(),
                                                                    status: 'assigned'
                                                                };
                                                                await addDoc(collection(db, 'speakerAssignments'), assignmentData);
                                                                showToast('Speaker assigned successfully!', 'success');
                                                            } catch (error) {
                                                                console.error('Error assigning speaker:', error);
                                                                showToast('Error assigning speaker', 'error');
                                                            }
                                                        }
                                                    });
                                                }}
                                            >
                                                <i className="fas fa-calendar-plus"></i> Assign
                                            </button>
                                        </div>
                                    </div>
                                    {/* Show speaker's recent reports */}
                                    {speakerReports.filter(r => r.speakerId === speaker.id).length > 0 && (
                                        <div className="mt-speaker-reports-preview">
                                            <details>
                                                <summary>Recent Reports ({speakerReports.filter(r => r.speakerId === speaker.id).length})</summary>
                                                <div className="mt-recent-table">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Date</th>
                                                                <th>People</th>
                                                                <th>Remarks</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {speakerReports
                                                                .filter(r => r.speakerId === speaker.id)
                                                                .slice(0, 5)
                                                                .map(r => (
                                                                    <tr key={r.id}>
                                                                        <td>{formatDate(r.date)}</td>
                                                                        <td>{r.remarks || 'N/A'}</td>
                                                                    </tr>
                                                                ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );

    // Render user dashboard
    const renderUserDashboard = () => (
        <div className="mt-user-dashboard">
            <div className="mt-user-header">
                <h2>My Dashboard</h2>
                <div className="mt-user-actions">
                    {userGroups.length > 0 && (
                        <button
                            className="mt-btn-primary"
                            onClick={() => {
                                setVisitationFormData({
                                    ...visitationFormData,
                                    groupId: userGroups[0]?.id || ''
                                });
                                setShowVisitationModal(true);
                            }}
                        >
                            <i className="fas fa-users"></i> Group Visitation
                        </button>
                    )}
                    <button
                        className="mt-btn-success"
                        onClick={() => setShowPersonalVisitationModal(true)}
                    >
                        <i className="fas fa-user-plus"></i> Personal Visitation
                    </button>
                    {isUserSpeaker() && (
                        <button
                            className="mt-btn-info"
                            onClick={() => {
                                const speaker = getUserSpeakerProfile();
                                if (speaker) {
                                    setSpeakerReportFormData({
                                        ...speakerReportFormData,
                                        speakerId: speaker.id
                                    });
                                    setShowSpeakerReportModal(true);
                                }
                            }}
                        >
                            <i className="fas fa-microphone"></i> Speaker Report
                        </button>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="mt-stats-grid">
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-layer-group"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">{userGroups.length}</div>
                        <div className="mt-stat-label">My Groups</div>
                    </div>
                </div>
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-walking"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">
                            {visitations.filter(v => userGroups.some(g => g.id === v.groupId)).length}
                        </div>
                        <div className="mt-stat-label">Group Visitations</div>
                    </div>
                </div>
                <div className="mt-stat-card">
                    <div className="mt-stat-icon"><i className="fas fa-user-plus"></i></div>
                    <div className="mt-stat-info">
                        <div className="mt-stat-value">{getUserPersonalVisitations().length}</div>
                        <div className="mt-stat-label">Personal Visitations</div>
                    </div>
                </div>
                {isUserSpeaker() && (
                    <div className="mt-stat-card">
                        <div className="mt-stat-icon"><i className="fas fa-microphone"></i></div>
                        <div className="mt-stat-info">
                            <div className="mt-stat-value">
                                {speakerReports.filter(r => r.speakerId === getUserSpeakerProfile()?.id).length}
                            </div>
                            <div className="mt-stat-label">Speaker Reports</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Groups */}
            <div className="mt-groups-section">
                <h3>My Mission Groups</h3>
                <div className="mt-groups-grid">
                    {userGroups.length === 0 ? (
                        <div className="mt-empty-state">
                            <i className="fas fa-users"></i>
                            <p>You are not assigned to any groups yet.</p>
                            <p className="mt-empty-sub">Contact your administrator to be added to a mission group.</p>
                        </div>
                    ) : (
                        userGroups.map(group => (
                            <div key={group.id} className="mt-group-card">
                                <div className="mt-group-header">
                                    <h3>{group.name}</h3>
                                    <span className={`mt-group-status ${group.status}`}>
                                        {group.status || 'active'}
                                    </span>
                                </div>
                                <div className="mt-group-body">
                                    <div className="mt-group-info">
                                        <div className="mt-group-info-item">
                                            <i className="fas fa-user-tie"></i>
                                            <span><strong>Leader:</strong> {group.leaderName || 'Not Assigned'}</span>
                                        </div>
                                        <div className="mt-group-info-item">
                                            <i className="fas fa-users"></i>
                                            <span><strong>Members:</strong> {group.memberNames?.join(', ') || 'None'}</span>
                                        </div>
                                        <div className="mt-group-info-item">
                                            <i className="fas fa-walking"></i>
                                            <span><strong>Visitations:</strong> {group.totalVisitations || 0}</span>
                                        </div>
                                        <div className="mt-group-info-item">
                                            <i className="fas fa-user-friends"></i>
                                            <span><strong>People Reached:</strong> {group.totalPeopleReached || 0}</span>
                                        </div>
                                    </div>
                                    <div className="mt-group-actions">
                                        <button
                                            className="mt-btn-small mt-btn-export"
                                            onClick={() => exportGroupReport(group.id)}
                                        >
                                            <i className="fas fa-file-pdf"></i> Report
                                        </button>
                                        <button
                                            className="mt-btn-small mt-btn-view"
                                            onClick={() => {
                                                setSelectedGroup(group);
                                                setShowMemberModal(true);
                                            }}
                                        >
                                            <i className="fas fa-eye"></i> View Details
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Personal Visitations */}
            <div className="mt-personal-section">
                <h3>My Personal Visitations</h3>
                {getUserPersonalVisitations().length === 0 ? (
                    <div className="mt-empty-state-small">
                        <p>No personal visitations recorded yet.</p>
                    </div>
                ) : (
                    <div className="mt-recent-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>People</th>
                                    <th>Ministry Area</th>
                                    <th>Location</th>
                                    <th>Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getUserPersonalVisitations().slice(0, 10).map(v => (
                                    <tr key={v.id}>
                                        <td>{formatDate(v.date)}</td>
                                        <td>{v.peopleCount}</td>
                                        <td>{v.ministryArea || 'General'}</td>
                                        <td>{v.location || 'N/A'}</td>
                                        <td>{v.remarks || 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    if (loading && members.length === 0) {
        return (
            <div className="mt-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading Mission Toolkit...</p>
            </div>
        );
    }

    return (
        <div className="mission-toolkit">
            <header className="mt-header">
                <div>
                    <h1>Mission Toolkit</h1>
                    <p className="mt-subtitle">Manage mission groups, personal visitations, and speaker assignments</p>
                </div>
                <div className="mt-header-actions">
                    {!isAdmin && (
                        <>
                            <button
                                className="mt-btn-primary mt-btn-float"
                                onClick={() => setShowPersonalVisitationModal(true)}
                            >
                                <i className="fas fa-user-plus"></i> Personal
                            </button>
                            {userGroups.length > 0 && (
                                <button
                                    className="mt-btn-success mt-btn-float"
                                    onClick={() => {
                                        setVisitationFormData({
                                            ...visitationFormData,
                                            groupId: userGroups[0]?.id || ''
                                        });
                                        setShowVisitationModal(true);
                                    }}
                                >
                                    <i className="fas fa-users"></i> Group
                                </button>
                            )}
                            {isUserSpeaker() && (
                                <button
                                    className="mt-btn-info mt-btn-float"
                                    onClick={() => {
                                        const speaker = getUserSpeakerProfile();
                                        if (speaker) {
                                            setSpeakerReportFormData({
                                                ...speakerReportFormData,
                                                speakerId: speaker.id
                                            });
                                            setShowSpeakerReportModal(true);
                                        }
                                    }}
                                >
                                    <i className="fas fa-microphone"></i> Speaker
                                </button>
                            )}
                        </>
                    )}
                </div>
            </header>

            {/* Tabs */}
            <div className="mt-tabs">
                <button
                    className={`mt-tab ${activeTab === 'groups' ? 'active' : ''}`}
                    onClick={() => setActiveTab('groups')}
                >
                    <i className="fas fa-user"></i> Personal
                </button>
                {isUserSpeaker() && (
                    <button
                        className={`mt-tab ${activeTab === 'speaker' ? 'active' : ''}`}
                        onClick={() => setActiveTab('speaker')}
                    >
                        <i className="fas fa-microphone"></i> Speaker
                    </button>
                )}
                {isAdmin && (
                    <button
                        className={`mt-tab ${activeTab === 'admin' ? 'active' : ''}`}
                        onClick={() => setActiveTab('admin')}
                    >
                        <i className="fas fa-chart-bar"></i> Admin Dashboard
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="mt-content">
                {activeTab === 'groups' && renderUserDashboard()}
                {activeTab === 'speaker' && isUserSpeaker() && renderSpeakerTab()}
                {activeTab === 'admin' && isAdmin && renderAdminDashboard()}
            </div>

            {/* Group Modal */}
            {showGroupModal && (
                <div className="mt-modal" onClick={() => setShowGroupModal(false)}>
                    <div className="mt-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="mt-modal-header">
                            <h3>{editingGroup ? 'Edit Group' : 'Create New Group'}</h3>
                            <button
                                className="mt-modal-close"
                                onClick={() => setShowGroupModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleGroupSubmit} className="mt-form">
                            <div className="mt-form-group">
                                <label>Group Name *</label>
                                <input
                                    type="text"
                                    className="mt-form-control"
                                    value={groupFormData.name}
                                    onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                                    placeholder="e.g., Team A - Nairobi"
                                    required
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Group Leader</label>
                                <select
                                    className="mt-form-control"
                                    value={groupFormData.leaderId}
                                    onChange={(e) => {
                                        const leaderId = e.target.value;
                                        setGroupFormData({
                                            ...groupFormData,
                                            leaderId: leaderId,
                                            leaderName: leaderId ? getUserName(leaderId) : ''
                                        });
                                    }}
                                >
                                    <option value="">Select Leader</option>
                                    {members.map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.fullName || member.firstName || member.email}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mt-form-group">
                                <label>Members (Select up to 4) *</label>
                                <div className="mt-members-select">
                                    {members.map(member => (
                                        <div key={member.id} className="mt-member-checkbox">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={groupFormData.members.includes(member.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            if (groupFormData.members.length >= 4) {
                                                                showToast('Maximum 4 members per group', 'error');
                                                                return;
                                                            }
                                                            setGroupFormData({
                                                                ...groupFormData,
                                                                members: [...groupFormData.members, member.id]
                                                            });
                                                        } else {
                                                            setGroupFormData({
                                                                ...groupFormData,
                                                                members: groupFormData.members.filter(id => id !== member.id)
                                                            });
                                                        }
                                                    }}
                                                />
                                                {member.fullName || member.firstName || member.email}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <small>{groupFormData.members.length}/4 members selected</small>
                            </div>

                            <div className="mt-form-row">
                                <div className="mt-form-group">
                                    <label>Location</label>
                                    <input
                                        type="text"
                                        className="mt-form-control"
                                        value={groupFormData.location}
                                        onChange={(e) => setGroupFormData({ ...groupFormData, location: e.target.value })}
                                        placeholder="e.g., Nairobi CBD"
                                    />
                                </div>
                                <div className="mt-form-group">
                                    <label>Focus Area</label>
                                    <input
                                        type="text"
                                        className="mt-form-control"
                                        value={groupFormData.focus}
                                        onChange={(e) => setGroupFormData({ ...groupFormData, focus: e.target.value })}
                                        placeholder="e.g., Outreach, Discipleship"
                                    />
                                </div>
                            </div>

                            <div className="mt-form-row">
                                <div className="mt-form-group">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        className="mt-form-control"
                                        value={groupFormData.startDate}
                                        onChange={(e) => setGroupFormData({ ...groupFormData, startDate: e.target.value })}
                                    />
                                </div>
                                <div className="mt-form-group">
                                    <label>End Date (Optional)</label>
                                    <input
                                        type="date"
                                        className="mt-form-control"
                                        value={groupFormData.endDate}
                                        onChange={(e) => setGroupFormData({ ...groupFormData, endDate: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="mt-form-actions">
                                <button
                                    type="button"
                                    className="mt-btn-secondary"
                                    onClick={() => setShowGroupModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="mt-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            {editingGroup ? 'Update' : 'Create'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Group Visitation Modal */}
            {showVisitationModal && (
                <div className="mt-modal" onClick={() => setShowVisitationModal(false)}>
                    <div className="mt-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="mt-modal-header">
                            <h3>Record Group Visitation</h3>
                            <button
                                className="mt-modal-close"
                                onClick={() => setShowVisitationModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleVisitationSubmit} className="mt-form">
                            <div className="mt-form-group">
                                <label>Group *</label>
                                <select
                                    className="mt-form-control"
                                    value={visitationFormData.groupId}
                                    onChange={(e) => setVisitationFormData({ ...visitationFormData, groupId: e.target.value })}
                                    required
                                >
                                    <option value="">Select Group</option>
                                    {(isAdmin ? groups : userGroups).map(group => (
                                        <option key={group.id} value={group.id}>
                                            {group.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mt-form-group">
                                <label>Number of People Visited *</label>
                                <input
                                    type="number"
                                    className="mt-form-control"
                                    value={visitationFormData.peopleCount}
                                    onChange={(e) => setVisitationFormData({ ...visitationFormData, peopleCount: e.target.value })}
                                    placeholder="0"
                                    min="0"
                                    required
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Date</label>
                                <input
                                    type="date"
                                    className="mt-form-control"
                                    value={visitationFormData.date}
                                    onChange={(e) => setVisitationFormData({ ...visitationFormData, date: e.target.value })}
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Location</label>
                                <input
                                    type="text"
                                    className="mt-form-control"
                                    value={visitationFormData.location}
                                    onChange={(e) => setVisitationFormData({ ...visitationFormData, location: e.target.value })}
                                    placeholder="Where did you visit?"
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Remarks</label>
                                <textarea
                                    className="mt-form-control"
                                    rows="3"
                                    value={visitationFormData.remarks}
                                    onChange={(e) => setVisitationFormData({ ...visitationFormData, remarks: e.target.value })}
                                    placeholder="Add any remarks or observations..."
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Challenges Faced</label>
                                <textarea
                                    className="mt-form-control"
                                    rows="2"
                                    value={visitationFormData.challenges}
                                    onChange={(e) => setVisitationFormData({ ...visitationFormData, challenges: e.target.value })}
                                    placeholder="Any challenges encountered..."
                                />
                            </div>

                            <div className="mt-form-actions">
                                <button
                                    type="button"
                                    className="mt-btn-secondary"
                                    onClick={() => setShowVisitationModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="mt-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i> Record Visitation
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Personal Visitation Modal */}
            {showPersonalVisitationModal && (
                <div className="mt-modal" onClick={() => setShowPersonalVisitationModal(false)}>
                    <div className="mt-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="mt-modal-header">
                            <h3>Record Personal Visitation</h3>
                            <button
                                className="mt-modal-close"
                                onClick={() => setShowPersonalVisitationModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handlePersonalVisitationSubmit} className="mt-form">
                            <div className="mt-form-group">
                                <label>Number of People Reached *</label>
                                <input
                                    type="number"
                                    className="mt-form-control"
                                    value={personalVisitationFormData.peopleCount}
                                    onChange={(e) => setPersonalVisitationFormData({ ...personalVisitationFormData, peopleCount: e.target.value })}
                                    placeholder="0"
                                    min="0"
                                    required
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Ministry Area</label>
                                <select
                                    className="mt-form-control"
                                    value={personalVisitationFormData.ministryArea}
                                    onChange={(e) => setPersonalVisitationFormData({ ...personalVisitationFormData, ministryArea: e.target.value })}
                                >
                                    <option value="General">General</option>
                                    <option value="Outreach">Outreach</option>
                                    <option value="Discipleship">Discipleship</option>
                                    <option value="Prayer">Prayer</option>
                                    <option value="Evangelism">Evangelism</option>
                                    <option value="Counseling">Counseling</option>
                                    <option value="Community Service">Community Service</option>
                                    <option value="Bible Study">Bible Study</option>
                                    <option value="Health">Health</option>
                                    <option value="Family Life">Family Life</option>
                                    <option value="Children">Children</option>
                                    <option value="Youth">Youth</option>
                                    <option value="Worship">Worship</option>
                                </select>
                            </div>

                            <div className="mt-form-group">
                                <label>Date</label>
                                <input
                                    type="date"
                                    className="mt-form-control"
                                    value={personalVisitationFormData.date}
                                    onChange={(e) => setPersonalVisitationFormData({ ...personalVisitationFormData, date: e.target.value })}
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Location</label>
                                <input
                                    type="text"
                                    className="mt-form-control"
                                    value={personalVisitationFormData.location}
                                    onChange={(e) => setPersonalVisitationFormData({ ...personalVisitationFormData, location: e.target.value })}
                                    placeholder="Where did you minister?"
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Remarks / Testimonies</label>
                                <textarea
                                    className="mt-form-control"
                                    rows="3"
                                    value={personalVisitationFormData.remarks}
                                    onChange={(e) => setPersonalVisitationFormData({ ...personalVisitationFormData, remarks: e.target.value })}
                                    placeholder="Share what happened, testimonies, or observations..."
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Challenges Faced</label>
                                <textarea
                                    className="mt-form-control"
                                    rows="2"
                                    value={personalVisitationFormData.challenges}
                                    onChange={(e) => setPersonalVisitationFormData({ ...personalVisitationFormData, challenges: e.target.value })}
                                    placeholder="Any challenges encountered during ministry..."
                                />
                            </div>

                            <div className="mt-form-actions">
                                <button
                                    type="button"
                                    className="mt-btn-secondary"
                                    onClick={() => setShowPersonalVisitationModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="mt-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i> Record Visitation
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
{/* Speaker Modal */}
{showSpeakerModal && (
    <div className="mt-modal" onClick={() => setShowSpeakerModal(false)}>
        <div className="mt-modal-content" onClick={e => e.stopPropagation()}>
            <div className="mt-modal-header">
                <h3>{editingSpeaker ? 'Edit Speaker' : 'Add Speaker from Members'}</h3>
                <button
                    className="mt-modal-close"
                    onClick={() => setShowSpeakerModal(false)}
                >
                    &times;
                </button>
            </div>
            <form onSubmit={handleSpeakerSubmit} className="mt-form">
                <div className="mt-form-group">
                    <label>Select Member *</label>
                    <select
                        className="mt-form-control"
                        value={speakerFormData.userId}
                        onChange={(e) => {
                            const userId = e.target.value;
                            const member = getMemberById(userId);
                            setSpeakerFormData({
                                ...speakerFormData,
                                userId: userId,
                                name: member?.fullName || member?.firstName || '',
                                email: member?.email || '',
                                phone: member?.phone || ''
                            });
                        }}
                        required
                        disabled={editingSpeaker}
                    >
                        <option value="">Select a member</option>
                        {members
                            .filter(member => {
                                // Check if member is already a speaker
                                const isAlreadySpeaker = speakers.some(s => s.userId === member.id);
                                // If editing, allow the current speaker's member
                                if (editingSpeaker) {
                                    const isCurrentSpeaker = speakers.some(s => s.id === editingSpeaker.id && s.userId === member.id);
                                    return !isAlreadySpeaker || isCurrentSpeaker;
                                }
                                return !isAlreadySpeaker;
                            })
                            .map(member => (
                                <option key={member.id} value={member.id}>
                                    {member.fullName || member.firstName || member.email} 
                                    {member.yearName ? ` (${member.yearName})` : ''}
                                </option>
                            ))}
                    </select>
                    {editingSpeaker && (
                        <small>Member selection cannot be changed after creation.</small>
                    )}
                </div>

                <div className="mt-form-group">
                    <label>Speaker Name</label>
                    <input
                        type="text"
                        className="mt-form-control"
                        value={speakerFormData.name}
                        disabled
                        placeholder="Auto-filled from member"
                    />
                    <small>Name is automatically taken from member profile.</small>
                </div>

                <div className="mt-form-group">
                    <label>Specialty</label>
                    <select
                        className="mt-form-control"
                        value={speakerFormData.specialty}
                        onChange={(e) => setSpeakerFormData({ ...speakerFormData, specialty: e.target.value })}
                    >
                        <option value="">Select Specialty</option>
                        {SPEAKER_SPECIALTIES.map(specialty => (
                            <option key={specialty} value={specialty}>
                                {specialty}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="mt-form-row">
                    <div className="mt-form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            className="mt-form-control"
                            value={speakerFormData.email}
                            disabled
                            placeholder="Auto-filled from member"
                        />
                    </div>
                    <div className="mt-form-group">
                        <label>Phone</label>
                        <input
                            type="text"
                            className="mt-form-control"
                            value={speakerFormData.phone}
                            disabled
                            placeholder="Auto-filled from member"
                        />
                    </div>
                </div>

                <div className="mt-form-group">
                    <label>Availability</label>
                    <div className="mt-toggle-group">
                        <label className="mt-toggle">
                            <input
                                type="checkbox"
                                checked={speakerFormData.availability}
                                onChange={(e) => setSpeakerFormData({ ...speakerFormData, availability: e.target.checked })}
                            />
                            <span className="mt-toggle-slider"></span>
                            <span className="mt-toggle-label">
                                {speakerFormData.availability ? 'Available' : 'Unavailable'}
                            </span>
                        </label>
                    </div>
                </div>

                <div className="mt-form-actions">
                    <button
                        type="button"
                        className="mt-btn-secondary"
                        onClick={() => setShowSpeakerModal(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="mt-btn-primary"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i> Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i>
                                {editingSpeaker ? 'Update' : 'Add Speaker'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    </div>
)}
    
            {/* Speaker Report Modal */}
            {showSpeakerReportModal && (
                <div className="mt-modal" onClick={() => setShowSpeakerReportModal(false)}>
                    <div className="mt-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="mt-modal-header">
                            <h3>Submit Speaker Report</h3>
                            <button
                                className="mt-modal-close"
                                onClick={() => setShowSpeakerReportModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleSpeakerReportSubmit} className="mt-form">
                            <div className="mt-form-group">
                                <label>Speaker *</label>
                                <select
                                    className="mt-form-control"
                                    value={speakerReportFormData.speakerId}
                                    onChange={(e) => setSpeakerReportFormData({ ...speakerReportFormData, speakerId: e.target.value })}
                                    required
                                >
                                    <option value="">Select Speaker</option>
                                    {(isAdmin ? speakers : speakers.filter(s => s.userId === currentUser.uid)).map(speaker => (
                                        <option key={speaker.id} value={speaker.id}>
                                            {speaker.name} {speaker.specialty ? `(${speaker.specialty})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mt-form-group">
                                <label>Date</label>
                                <input
                                    type="date"
                                    className="mt-form-control"
                                    value={speakerReportFormData.date}
                                    onChange={(e) => setSpeakerReportFormData({ ...speakerReportFormData, date: e.target.value })}
                                />
                            </div>

                          


                            <div className="mt-form-group">
                                <label>Remarks / Summary</label>
                                <textarea
                                    className="mt-form-control"
                                    rows="3"
                                    value={speakerReportFormData.remarks}
                                    onChange={(e) => setSpeakerReportFormData({ ...speakerReportFormData, remarks: e.target.value })}
                                    placeholder="Summary of the session, key points, testimonies..."
                                />
                            </div>

                            <div className="mt-form-group">
                                <label>Challenges Faced</label>
                                <textarea
                                    className="mt-form-control"
                                    rows="2"
                                    value={speakerReportFormData.challenges}
                                    onChange={(e) => setSpeakerReportFormData({ ...speakerReportFormData, challenges: e.target.value })}
                                    placeholder="Any challenges encountered during the session..."
                                />
                            </div>

                            
                            <div className="mt-form-actions">
                                <button
                                    type="button"
                                    className="mt-btn-secondary"
                                    onClick={() => setShowSpeakerReportModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="mt-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i> Submit Report
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Member Details Modal */}
            {showMemberModal && selectedGroup && (
                <div className="mt-modal" onClick={() => setShowMemberModal(false)}>
                    <div className="mt-modal-content mt-modal-large" onClick={e => e.stopPropagation()}>
                        <div className="mt-modal-header">
                            <h3>{selectedGroup.name} - Details</h3>
                            <button
                                className="mt-modal-close"
                                onClick={() => setShowMemberModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="mt-modal-body">
                            <div className="mt-member-details">
                                <div className="mt-member-info">
                                    <p><strong>Group Leader:</strong> {selectedGroup.leaderName || 'Not Assigned'}</p>
                                    <p><strong>Location:</strong> {selectedGroup.location || 'N/A'}</p>
                                    <p><strong>Focus:</strong> {selectedGroup.focus || 'N/A'}</p>
                                    <p><strong>Total Visitations:</strong> {selectedGroup.totalVisitations || 0}</p>
                                    <p><strong>People Reached:</strong> {selectedGroup.totalPeopleReached || 0}</p>
                                </div>

                                <div className="mt-member-list">
                                    <h4>Members ({selectedGroup.members?.length || 0})</h4>
                                    {selectedGroup.members?.map(memberId => {
                                        const member = members.find(m => m.id === memberId);
                                        const isSpeaker = speakers.some(s => s.userId === memberId);
                                        return (
                                            <div key={memberId} className="mt-member-item">
                                                <i className="fas fa-user"></i>
                                                <span>{member?.fullName || member?.firstName || memberId}</span>
                                                {member?.email && <small>({member.email})</small>}
                                                {isSpeaker && <span className="mt-speaker-badge"><i className="fas fa-microphone"></i> Speaker</span>}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="mt-group-visitations">
                                    <h4>Recent Visitations</h4>
                                    {visitations
                                        .filter(v => v.groupId === selectedGroup.id)
                                        .slice(0, 10)
                                        .map(v => (
                                            <div key={v.id} className="mt-visitation-item">
                                                <div className="mt-visitation-date">{formatDate(v.date)}</div>
                                                <div className="mt-visitation-details">
                                                    <span><strong>People:</strong> {v.peopleCount}</span>
                                                    {v.remarks && <span><strong>Remarks:</strong> {v.remarks}</span>}
                                                    {v.challenges && <span><strong>Challenges:</strong> {v.challenges}</span>}
                                                    <span className="mt-visitation-by">by {v.recordedByName}</span>
                                                </div>
                                            </div>
                                        ))}
                                    {visitations.filter(v => v.groupId === selectedGroup.id).length === 0 && (
                                        <p className="mt-no-visitations">No visitations recorded yet.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="mt-modal-footer">
                            <button
                                className="mt-btn-secondary"
                                onClick={() => setShowMemberModal(false)}
                            >
                                Close
                            </button>
                            <button
                                className="mt-btn-primary"
                                onClick={() => exportGroupReport(selectedGroup.id)}
                            >
                                <i className="fas fa-file-pdf"></i> Export Report
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MissionToolkit;
